import { Link, useParams } from "react-router-dom";
import ResourceCard from "@/components/feature/ResourceCard";
import usePageMeta from "@/hooks/usePageMeta";
import { animeResources, getResource } from "@/mocks/resources";

export default function ResourceDetail() {
  const { id = "" } = useParams();
  const resource = getResource(id);

  usePageMeta(
    resource ? `${resource.title} 全集资源 - 香蕉动漫资源合集` : "资源未找到 - 香蕉动漫",
    resource
      ? `${resource.title}动画全集资源合集，收录${resource.episodes}，${resource.summary}`
      : "你访问的资源不存在，返回香蕉动漫资源库继续找番。",
    resource ? `/library/${resource.id}` : "/library",
  );

  if (!resource) {
    return (
      <main className="mx-auto max-w-7xl px-4 md:px-6 pt-28 pb-16 text-center">
        <i className="ri-error-warning-line text-5xl text-foreground-400" aria-hidden="true"></i>
        <h1 className="mt-4 font-heading text-2xl text-foreground-950">未找到该资源</h1>
        <p className="mt-2 text-sm text-foreground-700">你访问的资源可能已下架或地址有误。</p>
        <Link
          to="/library"
          className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-foreground-950 text-sm font-medium hover:bg-primary-400 transition-colors whitespace-nowrap"
        >
          返回资源库
        </Link>
      </main>
    );
  }

  const related = animeResources
    .filter((r) => r.id !== resource.id && r.category === resource.category)
    .concat(animeResources.filter((r) => r.id !== resource.id && r.category !== resource.category))
    .slice(0, 4);

  return (
    <main className="mx-auto max-w-7xl px-4 md:px-6 pt-24 pb-4">
      {/* 面包屑 */}
      <nav aria-label="面包屑" className="text-sm text-foreground-600">
        <ol className="flex items-center gap-1 flex-wrap">
          <li><a href="/" className="hover:text-primary-600">首页</a></li>
          <li aria-hidden="true"><i className="ri-arrow-right-s-line"></i></li>
          <li><a href="/library" className="hover:text-primary-600">资源库</a></li>
          <li aria-hidden="true"><i className="ri-arrow-right-s-line"></i></li>
          <li aria-current="page" className="text-foreground-950 font-medium line-clamp-1">
            {resource.title}
          </li>
        </ol>
      </nav>

      {/* 详情主体 */}
      <section className="mt-6 grid grid-cols-1 md:grid-cols-[300px_1fr] gap-8 items-start">
        <div className="w-full max-w-[300px] mx-auto md:mx-0">
          <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-background-100">
            <img
              src={resource.cover}
              alt={`${resource.title} 全集资源封面`}
              title={`${resource.title} 全集资源`}
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>

        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-800 text-xs whitespace-nowrap">
              {resource.category}
            </span>
            {resource.tags.map((t) => (
              <span key={t} className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs whitespace-nowrap">
                #{t}
              </span>
            ))}
            <span
              className={`px-2.5 py-1 rounded-full text-xs whitespace-nowrap ${
                resource.status === "连载中"
                  ? "bg-accent-500 text-background-50"
                  : "bg-background-200 text-foreground-700"
              }`}
            >
              {resource.status}
            </span>
          </div>

          <h1 className="mt-3 font-heading text-2xl md:text-3xl text-foreground-950">{resource.title}</h1>

          <dl className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div className="bg-background-100 rounded-xl p-3">
              <dt className="text-xs text-foreground-600">首播年份</dt>
              <dd className="mt-1 font-medium text-foreground-950">{resource.year}</dd>
            </div>
            <div className="bg-background-100 rounded-xl p-3">
              <dt className="text-xs text-foreground-600">剧集规模</dt>
              <dd className="mt-1 font-medium text-foreground-950">{resource.episodes}</dd>
            </div>
            <div className="bg-background-100 rounded-xl p-3">
              <dt className="text-xs text-foreground-600">综合评分</dt>
              <dd className="mt-1 font-medium text-foreground-950 flex items-center gap-1">
                <i className="ri-star-fill text-primary-500" aria-hidden="true"></i>
                {resource.rating}
              </dd>
            </div>
            <div className="bg-background-100 rounded-xl p-3">
              <dt className="text-xs text-foreground-600">更新时间</dt>
              <dd className="mt-1 font-medium text-foreground-950">{resource.updateTime}</dd>
            </div>
          </dl>

          <p className="mt-5 text-sm text-foreground-800 leading-7">
            <strong className="text-foreground-950">作品简介：</strong>
            {resource.description}
          </p>

          <div className="mt-5">
            <h2 className="font-heading text-base text-foreground-950">资源亮点</h2>
            <ul className="mt-3 space-y-2">
              {resource.highlights.map((h) => (
                <li key={h} className="flex items-center gap-2 text-sm text-foreground-800">
                  <i className="ri-checkbox-circle-fill text-primary-500" aria-hidden="true"></i>
                  {h}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-6 bg-accent-100/70 border border-accent-200 rounded-xl px-5 py-4 text-sm text-foreground-800 leading-6">
            <i className="ri-information-line text-accent-700" aria-hidden="true"></i>{" "}
            <strong className="text-foreground-950">资源说明：</strong>
            {resource.sourceNote}
          </div>
        </div>
      </section>

      {/* 相关推荐 */}
      <section className="mt-12">
        <h2 className="font-heading text-xl md:text-2xl text-foreground-950">你可能还想看</h2>
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {related.map((r) => (
            <ResourceCard key={r.id} resource={r} />
          ))}
        </div>
      </section>
    </main>
  );
}